<div class="container py-5">
  <!-- Toast Notification -->
  <div
    *ngIf="showToast"
    class="position-fixed top-0 start-50 translate-middle-x mt-3"
    style="z-index: 9999"
  >
    <div
      class="alert alert-dismissible fade show shadow-lg"
      [ngClass]="{
        'alert-success': toastType === 'success',
        'alert-danger': toastType === 'error'
      }"
      role="alert"
    >
      <i
        class="me-2"
        [class.bi-check-circle-fill]="toastType === 'success'"
        [class.bi-exclamation-circle-fill]="toastType === 'error'"
      ></i>
      {{ toastMessage }}
      <button
        type="button"
        class="btn-close"
        (click)="closeToast()"
        aria-label="Close"
      ></button>
    </div>
  </div>

  <!-- Login Card -->
  <div class="row justify-content-center">
    <div class="col-12 col-md-8 col-lg-6">
      <div class="card shadow-lg border-0">
        <!-- Header -->
        <div class="card-header bg-linear-gradient-purple text-white text-center border-0 p-4">
          <div class="mb-3">
            <i class="bi bi-shield-lock" style="font-size: 3rem;"></i>
          </div>
          <h3 class="mb-1 fw-bold">Employee Login</h3>
          <p class="mb-0 opacity-75">Choose your preferred login method</p>
        </div>

        <!-- Login Method Toggle -->
        <div class="card-body p-4">
          <div class="btn-group w-100 mb-4 shadow-sm" role="group" aria-label="Login method selection">
            <button
              type="button"
              class="btn btn-lg py-3"
              [class.btn-gradient]="loginMethod === 'pin'"
              [class.btn-outline-secondary]="loginMethod !== 'pin'"
              (click)="switchLoginMethod('pin')"
              [disabled]="isLoading"
            >
              <i class="bi bi-lock-fill me-2"></i>
              <span class="d-none d-sm-inline">PIN Login</span>
              <span class="d-inline d-sm-none">PIN</span>
            </button>
            <button
              type="button"
              class="btn btn-lg py-3"
              [class.btn-gradient]="loginMethod === 'email'"
              [class.btn-outline-secondary]="loginMethod !== 'email'"
              (click)="switchLoginMethod('email')"
              [disabled]="isLoading"
            >
              <i class="bi bi-envelope-fill me-2"></i>
              <span class="d-none d-sm-inline">Email Login</span>
              <span class="d-inline d-sm-none">Email</span>
            </button>
          </div>

          <!-- PIN Login Form -->
          <div *ngIf="loginMethod === 'pin'" class="login-form-container">
            <div class="text-center mb-4">
              <h5 class="text-muted">
                <i class="bi bi-building me-2"></i>Login with PIN
              </h5>
              <small class="text-muted">Select your store and enter your PIN</small>
            </div>

            <form [formGroup]="pinForm" (ngSubmit)="onPinSubmit()">
              <!-- Organization Selection -->
              <div class="mb-3">
                <label for="org" class="form-label fw-semibold">
                  <i class="bi bi-building me-2 text-primary"></i>Store Location
                </label>
                <select
                  id="org"
                  class="form-select form-select-lg"
                  formControlName="orgId"
                  [disabled]="isLoading"
                >
                  <option value="" disabled>Choose your store...</option>
                  <option *ngFor="let org of orgs$ | async" [value]="org.id">
                    {{ org.name }}
                  </option>
                </select>
              </div>

              <!-- Loading Employees -->
              <div *ngIf="isLoadingEmployees" class="text-center py-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading employees...</span>
                </div>
                <p class="text-muted mt-2 mb-0">Loading employees...</p>
              </div>

              <!-- Employee Selection -->
              <div class="mb-3" *ngIf="showEmployeeDropdown">
                <label for="employee" class="form-label fw-semibold">
                  <i class="bi bi-person me-2 text-primary"></i>Your Name
                </label>
                <select
                  id="employee"
                  class="form-select form-select-lg"
                  formControlName="employeeId"
                  [disabled]="isLoading"
                >
                  <option value="" disabled>Select your name...</option>
                  <option *ngFor="let emp of employees" [value]="emp.id">
                    {{ emp.Name }}
                  </option>
                </select>
              </div>

              <!-- Placeholder -->
              <div
                *ngIf="!pinForm.value.orgId && !isLoadingEmployees"
                class="alert alert-info text-center"
              >
                <i class="bi bi-arrow-up-circle me-2"></i>
                Please select your store to continue
              </div>

              <!-- PIN Input -->
              <div class="mb-4" *ngIf="showPinField">
                <label for="pin" class="form-label fw-semibold">
                  <i class="bi bi-shield-lock me-2 text-primary"></i>Security PIN
                </label>
                <div class="input-group input-group-lg">
                  <span class="input-group-text bg-light">
                    <i class="bi bi-lock"></i>
                  </span>
                  <input
                    [type]="showPin ? 'text' : 'password'"
                    inputmode="numeric"
                    maxlength="6"
                    class="form-control"
                    id="pin"
                    placeholder="Enter 4-6 digit PIN"
                    formControlName="pin"
                    [class.is-invalid]="
                      pinForm.controls.pin.invalid && pinForm.controls.pin.touched
                    "
                    [disabled]="isLoading"
                    autocomplete="off"
                  />
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    (click)="togglePinVisibility()"
                    [disabled]="isLoading"
                    aria-label="Toggle PIN visibility"
                  >
                    <i [class]="showPin ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>
                <div
                  *ngIf="
                    pinForm.controls.pin.invalid &&
                    pinForm.controls.pin.touched &&
                    pinForm.controls.pin.errors?.['pattern']
                  "
                  class="text-danger mt-2 small"
                >
                  <i class="bi bi-exclamation-circle me-1"></i>
                  PIN must be 4-6 digits only
                </div>
              </div>

              <!-- Submit Button -->
              <button
                type="submit"
                class="btn btn-gradient w-100 btn-lg fw-bold py-3"
                [disabled]="isLoading || pinForm.invalid"
              >
                <span *ngIf="!isLoading">
                  <i class="bi bi-box-arrow-in-right me-2"></i>Login with PIN
                </span>
                <span *ngIf="isLoading">
                  <span class="spinner-border spinner-border-sm me-2"></span>
                  Logging in...
                </span>
              </button>
            </form>
          </div>

          <!-- Email Login Form -->
          <div *ngIf="loginMethod === 'email'" class="login-form-container">
            <div class="text-center mb-4">
              <h5 class="text-muted">
                <i class="bi bi-envelope me-2"></i>Login with Email
              </h5>
              <small class="text-muted">Enter your email address and password</small>
            </div>

            <form [formGroup]="emailForm" (ngSubmit)="onEmailSubmit()">
              <!-- Email Input -->
              <div class="mb-3">
                <label for="email" class="form-label fw-semibold">
                  <i class="bi bi-envelope me-2 text-primary"></i>Email Address
                </label>
                <div class="input-group input-group-lg">
                  <span class="input-group-text bg-light">
                    <i class="bi bi-at"></i>
                  </span>
                  <input
                    type="email"
                    class="form-control"
                    id="email"
                    placeholder="your.email@example.com"
                    formControlName="email"
                    [class.is-invalid]="
                      emailForm.controls.email.invalid &&
                      emailForm.controls.email.touched
                    "
                    [disabled]="isLoading"
                    autocomplete="email"
                  />
                </div>
                <div
                  *ngIf="
                    emailForm.controls.email.invalid &&
                    emailForm.controls.email.touched
                  "
                  class="text-danger mt-2 small"
                >
                  <i class="bi bi-exclamation-circle me-1"></i>
                  <span *ngIf="emailForm.controls.email.errors?.['required']">
                    Email is required
                  </span>
                  <span *ngIf="emailForm.controls.email.errors?.['email']">
                    Please enter a valid email address
                  </span>
                </div>
              </div>

              <!-- Password Input -->
              <div class="mb-4">
                <label for="password" class="form-label fw-semibold">
                  <i class="bi bi-key me-2 text-primary"></i>Password
                </label>
                <div class="input-group input-group-lg">
                  <span class="input-group-text bg-light">
                    <i class="bi bi-key"></i>
                  </span>
                  <input
                    [type]="showPassword ? 'text' : 'password'"
                    class="form-control"
                    id="password"
                    placeholder="Enter your password"
                    formControlName="password"
                    [class.is-invalid]="
                      emailForm.controls.password.invalid &&
                      emailForm.controls.password.touched
                    "
                    [disabled]="isLoading"
                    autocomplete="current-password"
                  />
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    (click)="togglePasswordVisibility()"
                    [disabled]="isLoading"
                    aria-label="Toggle password visibility"
                  >
                    <i [class]="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>
                <div
                  *ngIf="
                    emailForm.controls.password.invalid &&
                    emailForm.controls.password.touched
                  "
                  class="text-danger mt-2 small"
                >
                  <i class="bi bi-exclamation-circle me-1"></i>
                  <span *ngIf="emailForm.controls.password.errors?.['required']">
                    Password is required
                  </span>
                  <span *ngIf="emailForm.controls.password.errors?.['minlength']">
                    Password must be at least 6 characters
                  </span>
                </div>
              </div>

              <!-- Submit Button -->
              <button
                type="submit"
                class="btn btn-gradient w-100 btn-lg fw-bold py-3"
                [disabled]="isLoading || emailForm.invalid"
              >
                <span *ngIf="!isLoading">
                  <i class="bi bi-box-arrow-in-right me-2"></i>Login with Email
                </span>
                <span *ngIf="isLoading">
                  <span class="spinner-border spinner-border-sm me-2"></span>
                  Logging in...
                </span>
              </button>
            </form>
          </div>
        </div>

        <!-- Footer -->
        <div class="card-footer bg-light text-center border-0 py-3">
          <small class="text-muted">
            <i class="bi bi-shield-check me-1"></i>
            Your login is secure and encrypted with bcrypt
          </small>
        </div>
      </div>
    </div>
  </div>
</div>